import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Page de redirection vers le deep link meyebus:// pour les actions mobiles.
 * Utilisee comme intermediaire HTTPS dans les emails (les clients mail bloquent les schemes custom).
 *
 * Usage: https://meyebus.ma/app/reset-password?token=xxx
 *   -> tente d'ouvrir meyebus://reset-password?token=xxx
 *   -> si l'app n'est pas installee, affiche un message avec lien vers les stores
 */
export default function AppRedirectPage({ action }) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('redirecting'); // 'redirecting' | 'fallback'
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('fallback');
      return;
    }

    // Construire le deep link selon l'action
    let deepLink = '';
    if (action === 'reset-password') {
      deepLink = `meyebus://reset-password?token=${token}`;
    } else {
      setStatus('fallback');
      return;
    }

    // Tenter d'ouvrir l'app via le deep link
    window.location.href = deepLink;

    // Apres 2.5s, si l'app n'a pas intercepte le lien, afficher le fallback
    const timer = setTimeout(() => {
      setStatus('fallback');
    }, 2500);

    return () => clearTimeout(timer);
  }, [token, action]);

  if (status === 'redirecting') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>M'EyeBus</div>
          <div style={styles.spinner} />
          <p style={styles.title}>Ouverture de l'application...</p>
          <p style={styles.subtitle}>Vous allez être redirigé vers l'application M'EyeBus.</p>
        </div>
      </div>
    );
  }

  // Fallback : app non installée ou lien invalide
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>M'EyeBus</div>
        {token ? (
          <>
            <p style={styles.title}>Application non détectée</p>
            <p style={styles.subtitle}>
              L'application M'EyeBus ne semble pas être installée sur cet appareil. Téléchargez-la pour réinitialiser
              votre mot de passe.
            </p>
            <div style={styles.storeLinks}>
              <a href="https://play.google.com/store/apps/details?id=com.schoolbus.simple" style={styles.storeButton}>
                Google Play
              </a>
              <a href="https://apps.apple.com/us/app/meyebus/id6760791310" style={styles.storeButton}>
                App Store
              </a>
            </div>
            <p style={styles.hint}>
              Après installation, revenez sur ce lien ou refaites une demande de réinitialisation.
            </p>
          </>
        ) : (
          <>
            <p style={styles.title}>Lien invalide</p>
            <p style={styles.subtitle}>Ce lien est invalide ou a expiré.</p>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
    fontFamily: 'Arial, sans-serif',
    padding: '24px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '48px 40px',
    maxWidth: '420px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  logo: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#0B2340',
    marginBottom: '32px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 24px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: '12px',
  },
  subtitle: {
    fontSize: '15px',
    color: '#64748b',
    lineHeight: '1.6',
    marginBottom: '24px',
  },
  storeLinks: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  storeButton: {
    background: '#0B2340',
    color: '#ffffff',
    padding: '12px 20px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  hint: {
    fontSize: '13px',
    color: '#94a3b8',
    lineHeight: '1.5',
  },
};
